import type { ReaderParagraph } from "@/data/curatedChapters";

export type EnhancementScope = "spread" | "chapter" | "book";

export type ChapterEnhanceChunk = { index: number; text: string };

export type ChapterEnhanceManifest = {
  job_id: string;
  book_id: string;
  chapter_id: string;
  preset: EnhancementPreset;
  custom_prompt: string;
  style_prompt: string;
  style_label: string;
  status: "processing" | "complete" | "error";
  chunks_total: number;
  chunks_ready: number;
  enhanced_chunks: ChapterEnhanceChunk[];
  error?: string;
};

export type EnhancementPreset =
  | "eli5"
  | "modern"
  | "dramatic"
  | "pirate"
  | "bedtime"
  | "spooky"
  | "funny"
  | "shakespeare"
  | "study"
  | "custom";

export type EnhancementStatus = "idle" | "generating" | "ready" | "error";

export type CachedEnhancement = {
  enhancedText: string;
  styleLabel: string;
  /** When set (chapter job), per-paragraph enhanced text aligned to `ReaderParagraph[]` indices. */
  paragraphTexts?: string[];
};

export const ENHANCEMENT_PRESET_OPTIONS: { value: EnhancementPreset; label: string }[] = [
  { value: "eli5", label: "Read it like I'm 5" },
  { value: "modern", label: "Modern language" },
  { value: "dramatic", label: "Dramatic" },
  { value: "pirate", label: "Pirate style" },
  { value: "bedtime", label: "Bedtime story" },
  { value: "spooky", label: "Spooky campfire" },
  { value: "funny", label: "Funny / silly" },
  { value: "shakespeare", label: "Shakespearean" },
  { value: "study", label: "Study guide" },
  { value: "custom", label: "Custom" },
];

export function enhancementCacheKey(
  bookId: string,
  chapterSlug: string,
  flatLeftIndex: number,
  preset: EnhancementPreset,
  customPrompt: string,
): string {
  const safeBook = String(bookId ?? "").trim();
  const safeChapter = String(chapterSlug ?? "").trim();
  const custom = preset === "custom" ? String(customPrompt ?? "").trim().slice(0, 4000) : "";
  return `${safeBook}::${safeChapter}::${flatLeftIndex}::${preset}::${custom}`;
}

export function chapterEnhancementCacheKey(
  bookId: string,
  chapterSlug: string,
  preset: EnhancementPreset,
  customPrompt: string,
): string {
  const safeBook = String(bookId ?? "").trim();
  const safeChapter = String(chapterSlug ?? "").trim();
  const custom = preset === "custom" ? String(customPrompt ?? "").trim().slice(0, 4000) : "";
  return `${safeBook}::${safeChapter}::__chapter__::${preset}::${custom}`;
}

const DEFAULT_CHUNK_CHARS = 3200;

/** Group consecutive paragraphs into chunks for sequential enhancement (one shared style_prompt). */
export function buildChapterEnhanceChunks(
  paragraphs: ReaderParagraph[],
  maxCharsPerChunk: number = DEFAULT_CHUNK_CHARS,
): { startParagraphIndex: number; endParagraphIndex: number; text: string }[] {
  const list = Array.isArray(paragraphs) ? paragraphs : [];
  if (list.length === 0) return [];
  const cap = Number.isFinite(maxCharsPerChunk) && maxCharsPerChunk > 400 ? maxCharsPerChunk : DEFAULT_CHUNK_CHARS;
  const chunks: { startParagraphIndex: number; endParagraphIndex: number; text: string }[] = [];
  let start = 0;
  let buf: string[] = [];
  let run = 0;
  const flush = (end: number) => {
    if (buf.length === 0) return;
    const text = buf.join("\n\n");
    chunks.push({ startParagraphIndex: start, endParagraphIndex: end, text });
    buf = [];
    run = 0;
  };
  for (let i = 0; i < list.length; i++) {
    const p = list[i];
    const piece = (p.label ? `${p.label}: ` : "") + p.text;
    const add = piece.length + (buf.length ? 2 : 0);
    if (run + add > cap && buf.length > 0) {
      flush(i - 1);
    }
    if (buf.length === 0) start = i;
    buf.push(piece);
    run += add;
  }
  if (buf.length > 0) flush(list.length - 1);
  return chunks;
}

/** Split model output across N paragraph slots (double-newline paragraphs). */
export function distributeEnhancedChunkAcrossParagraphCount(paragraphCount: number, enhancedBlob: string): string[] {
  const n = Math.max(1, paragraphCount);
  const raw = String(enhancedBlob ?? "").trim();
  if (!raw) return Array.from({ length: n }, () => "");
  let parts = raw
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  if (parts.length === 0) parts = [raw];
  if (parts.length === n) return parts;
  if (parts.length < n) {
    const out = [...parts];
    while (out.length < n) out.push("");
    return out;
  }
  const head = parts.slice(0, n - 1);
  head.push(parts.slice(n - 1).join("\n\n"));
  return head;
}

export function mergeEnhancedChunkIntoParallel(
  parallel: (string | null)[],
  startParagraphIndex: number,
  endParagraphIndex: number,
  enhancedBlob: string,
): void {
  const n = endParagraphIndex - startParagraphIndex + 1;
  const parts = distributeEnhancedChunkAcrossParagraphCount(n, enhancedBlob);
  for (let i = 0; i < n; i++) {
    const gi = startParagraphIndex + i;
    if (gi >= 0 && gi < parallel.length) parallel[gi] = parts[i] ?? "";
  }
}

export function chapterPlainTextFromParagraphs(paragraphs: ReaderParagraph[]): string {
  return paragraphs.map((p) => (p.label ? `${p.label}: ` : "") + p.text).join("\n\n");
}

/** Swap in enhanced text by global paragraph index; keep labels from originals. */
export function applyParagraphEnhancementsToBlocks(
  blocks: ReaderParagraph[],
  startGlobalParagraphIndex: number,
  originals: ReaderParagraph[],
  paragraphTexts: string[],
): ReaderParagraph[] {
  if (!paragraphTexts.length || paragraphTexts.length !== originals.length) return blocks;
  return blocks.map((blk, i) => {
    const gi = startGlobalParagraphIndex + i;
    const t = paragraphTexts[gi];
    if (typeof t !== "string" || !t.trim()) return blk;
    return { ...blk, text: t };
  });
}

/** Instructions sent to the enhancement model (plot-preserving, tone-only). */
export function presetToStyleDirective(preset: EnhancementPreset, customPrompt: string): string {
  const custom = String(customPrompt ?? "").trim();
  const baseRules =
    "Preserve plot, events, facts, sequence, and character intent. Adapt wording, tone, and style only. " +
    "Do not invent major new story events. Keep roughly similar length unless the style requires brevity (e.g. ELI5).";

  const byPreset: Record<Exclude<EnhancementPreset, "custom">, string> = {
    eli5: `${baseRules} Rewrite in very simple vocabulary and short sentences suitable for a young child.`,
    modern: `${baseRules} Rewrite in clear, contemporary English (plain modern language).`,
    dramatic: `${baseRules} Heighten tension and emotional color with vivid but accurate wording; stay faithful to events.`,
    pirate: `${baseRules} Add light nautical flavor and occasional pirate color; keep most sentences in clear, natural English—avoid turning every line into parody or jargon.`,
    bedtime: `${baseRules} Gentle, warm, soothing bedtime-story tone; soft pacing, still accurate.`,
    spooky: `${baseRules} Eerie campfire-tale atmosphere; mood and word choice only—no new horrors or plot twists.`,
    funny: `${baseRules} Light, silly, comedic tone while keeping the same story beats.`,
    shakespeare: `${baseRules} Early Modern English flavor (thee/thou sparingly); do not add new scenes; keep events clear.`,
    study: `${baseRules} Study-friendly clarity with brief inline explanations; avoid section headings and bullet lists unless the original had them.`,
  };

  if (preset === "custom") {
    if (!custom) return `${baseRules} Improve clarity and engagement slightly without changing meaning.`;
    return `${baseRules} Reader custom direction: ${custom}`;
  }
  return byPreset[preset];
}

export function defaultStyleLabelForPreset(preset: EnhancementPreset): string {
  const row = ENHANCEMENT_PRESET_OPTIONS.find((o) => o.value === preset);
  return row?.label ?? "Enhanced";
}

/** Split enhanced prose across open spread (two columns when wide). Paragraph labels are empty; show style once in the reader chrome (e.g. footnote). */
export function enhancedSpreadToDisplayPages(
  enhancedText: string,
  _styleLabel: string,
  wide: boolean,
): { left: ReaderParagraph[]; right: ReaderParagraph[] | null } {
  const raw = String(enhancedText ?? "").replace(/\r\n/g, "\n").trim();
  const chunks = raw
    .split(/\n{2,}/)
    .map((s) => s.trim())
    .filter((s) => s.length > 0);
  const blocks: ReaderParagraph[] =
    chunks.length > 0 ? chunks.map((text) => ({ label: "", text })) : [{ label: "", text: raw }];

  if (!wide) {
    return { left: blocks, right: null };
  }
  if (blocks.length <= 1) {
    return { left: blocks, right: [] };
  }
  const mid = Math.ceil(blocks.length / 2);
  return { left: blocks.slice(0, mid), right: blocks.slice(mid) };
}

type JsonObject = Record<string, unknown>;

function isObject(value: unknown): value is JsonObject {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

export function parseEnhanceWebhookPayload(
  payload: unknown,
): { ok: true; text: string; styleLabel: string } | { ok: false; error: string } {
  if (!isObject(payload)) return { ok: false, error: "Invalid response" };
  if (payload.success === false) {
    const err =
      typeof payload.error === "string" && payload.error.trim()
        ? payload.error.trim()
        : typeof payload.message === "string" && payload.message.trim()
          ? payload.message.trim()
          : "Enhancement failed";
    return { ok: false, error: err };
  }
  const text = typeof payload.enhanced_text === "string" ? payload.enhanced_text.trim() : "";
  if (!text) return { ok: false, error: "No enhanced_text in response" };
  const styleLabel =
    typeof payload.style_label === "string" && payload.style_label.trim().length > 0
      ? payload.style_label.trim()
      : "Enhanced";
  return { ok: true, text, styleLabel };
}

/** Pass A only (`enhance_phase: style_only`). */
export function parseEnhanceStyleOnlyPayload(
  payload: unknown,
): { ok: true; stylePrompt: string; styleLabel: string } | { ok: false; error: string } {
  if (!isObject(payload)) return { ok: false, error: "Invalid response" };
  if (payload.success === false) {
    const err =
      typeof payload.error === "string" && payload.error.trim()
        ? payload.error.trim()
        : typeof payload.message === "string" && payload.message.trim()
          ? payload.message.trim()
          : "Style prompt request failed";
    return { ok: false, error: err };
  }
  const stylePrompt = typeof payload.style_prompt === "string" ? payload.style_prompt.trim() : "";
  if (!stylePrompt) return { ok: false, error: "No style_prompt in response" };
  const styleLabel =
    typeof payload.style_label === "string" && payload.style_label.trim().length > 0
      ? payload.style_label.trim()
      : "Enhanced";
  return { ok: true, stylePrompt, styleLabel };
}

/** Pass B chunk (`enhance_phase: transform_only`). */
export function parseEnhanceTransformChunkPayload(
  payload: unknown,
): { ok: true; text: string; styleLabel: string } | { ok: false; error: string } {
  return parseEnhanceWebhookPayload(payload);
}

